import { inject, NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import {
    SupportIssuesPage,
    UserGuard,
    ContentPageComponent,
    ThemeService,
    DebugPanelComponent,
    InviteRedeemPage,
    EventBrowsePage
} from 'army-builder-shared';

import { GameListPage } from './home/game-list.page';
import { HomePage } from './home/home.page';
import { GAME_IDS } from './game-id.token';
import { SettingsShellComponent } from './settings/shell/settings-shell.component';
import { SettingsMenuComponent } from './settings/menu/settings-menu.component';
import { AccountSettingsPage } from './settings/account/account-settings.page';
import { DevicesSettingsPage } from './settings/devices/devices-settings.page';
import { AccountActionsPage } from './settings/actions/account-actions.page';
import { SubscriptionSettingsPage } from './settings/subscription-settings.page';
import { EULAPage } from 'army-builder-shared/src/lib/global/settings/eula.page';
import { RegistrationPage } from 'army-builder-shared/src/lib/global/settings/registration/registration.page';
import { VerifyEmailPage } from 'army-builder-shared/src/lib/global/settings/verify-email/verify-email.page';
import { ResetPasswordRequestPage } from 'army-builder-shared/src/lib/global/settings/reset-password/reset-password-request.page';
import { ResetPasswordConfirmPage } from 'army-builder-shared/src/lib/global/settings/reset-password/reset-password-confirm.page';
import { LoginPage } from './settings/user/login.page';

const routes: Routes = [
    {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full'
    },
    {
        path: 'home',
        component: HomePage,
        data: {
            gameId: 'home'
        }
    },
    {
        path: 'pages/:pageId',
        component: ContentPageComponent
    },
    {
        path: 'eula',
        component: EULAPage
    },
    {
        path: 'login',
        component: LoginPage,
        data: { theme: 'auth home' }
    },
    {
        path: 'register',
        component: RegistrationPage,
        data: { theme: 'auth home' }
    },
    {
        path: 'verify-email',
        component: VerifyEmailPage,
        data: { theme: 'auth home' }
    },
    {
        path: 'reset-password',
        component: ResetPasswordRequestPage,
        data: { theme: 'auth home' }
    },
    {
        path: 'reset-password/confirm',
        component: ResetPasswordConfirmPage,
        data: { theme: 'auth home' }
    },
    {
        path: 'games',
        component: GameListPage,
        canActivate: [UserGuard],
        data: {
            gameId: 'home',
            requiredRoles: ['app_subscriber', 'app_subscriber_annual']
        }
    },
    {
        // Top-level, game-agnostic events browse page — deliberately no UserGuard/requiredRoles,
        // same reasoning as the per-game events routes (see the comment on the bolt-action-3e
        // route below): reachable by an anonymous visitor, not gated on subscription. data.gameId
        // 'home' is what gives it the same neutral background as /home and /games (ThemeService).
        path: 'events',
        component: EventBrowsePage,
        data: {
            gameId: 'home'
        }
    },
    {
        path: 'admin',
        loadChildren: () => import('./admin/admin.module').then((m) => m.AdminModule),
        data: {
            gameId: 'home2'
        }
    },
    {
        path: GAME_IDS.BOLT_ACTION_3E,
        // No top-level UserGuard here on purpose: login/subscription requirements
        // are already applied per-route inside BOLT_ACTION_ROUTES (forces/library/
        // collection-manager require UserGuard; events and settings deliberately
        // don't, so they — including a shared event link — stay reachable by an
        // anonymous visitor). A guard here would override that and block the whole
        // module regardless of which child route was actually requested, which is
        // exactly what was happening to a shared event link before this comment.
        loadChildren: () => import('./bolt-action/bolt-action-3e.module').then((m) => m.BoltAction3eModule),
        data: {
            gameId: GAME_IDS.BOLT_ACTION_3E,
            theme: 'bolt-action'
        }
    },
    {
        path: GAME_IDS.K47,
        // See the identical comment on BOLT_ACTION_3E above — K47_ROUTES is built
        // from BOLT_ACTION_ROUTES and inherits the same per-route guarding.
        loadChildren: () => import('./k47/k47.module').then((m) => m.K47Module),
        data: {
            // requiredRoles: ['k47-early-access'],
            gameId: GAME_IDS.K47
        }
    },
    {
        path: 'black-seas',
        canActivate: [UserGuard],
        loadChildren: () => import('./black-seas/black-seas.module').then((m) => m.BlackSeasModule),
        data: {
            requiredRoles: ['app_subscriber', 'app_subscriber_annual'],
            gameId: 'black-seas'
        }
    },
    {
        path: 'blood-red-skies',
        canActivate: [UserGuard],
        loadChildren: () => import('./blood-red-skies/blood-red-skies.module').then((m) => m.BloodRedSkiesModule),
        data: {
            requiredRoles: ['app_subscriber', 'app_subscriber_annual'],
            gameId: 'blood-red-skies'
        }
    },
    {
        path: 'settings',
        component: SettingsShellComponent,
        children: [
            {
                // Desktop renders the menu as a persistent sidebar, so the bare /settings index
                // would leave the content pane empty; send it straight to the first section.
                // Mobile keeps the menu as a drill-down list, on its own path so this stays a
                // plain routing redirect.
                path: '',
                pathMatch: 'full',
                redirectTo: () => (inject(ThemeService).isDesktop() ? 'account' : 'menu')
            },
            {
                path: 'menu',
                canActivate: [UserGuard],
                component: SettingsMenuComponent,
                data: { trackingId: 'Settings' }
            },
            {
                path: 'account',
                canActivate: [UserGuard],
                component: AccountSettingsPage,
                data: { trackingId: 'Settings > Account' }
            },
            {
                path: 'devices',
                canActivate: [UserGuard],
                component: DevicesSettingsPage,
                data: { trackingId: 'Settings > Devices' }
            },
            {
                path: 'subscription',
                canActivate: [UserGuard],
                component: SubscriptionSettingsPage,
                data: { trackingId: 'Settings > Subscription' }
            },
            {
                path: 'actions',
                canActivate: [UserGuard],
                component: AccountActionsPage,
                data: { trackingId: 'Settings > Account Actions' }
            },
            {
                // DebugPanelComponent rather than DebugModule: the shell already supplies the
                // abs-page-layout, and DebugComponent would nest a second one inside it.
                path: 'debug',
                component: DebugPanelComponent,
                data: { trackingId: 'Settings > Debug' }
            }
        ],
        data: {
            gameId: 'home'
        }
    },
    {
        path: 'subscription',
        loadChildren: () => import('./subscription/subscription.module').then((m) => m.SubscriptionModule),
        data: {
            gameId: 'home'
        }
    },
    {
        path: 'support',
        canActivate: [UserGuard],
        component: SupportIssuesPage,
        data: {
            gameId: 'home',
            mode: 'user'
        }
    },
    {
        path: 'invite/:code',
        canActivate: [UserGuard],
        component: InviteRedeemPage,
        data: {
            gameId: 'home'
        }
    }
];

@NgModule({
    imports: [RouterModule.forRoot(routes, {})],
    exports: [RouterModule]
})
export class AppRoutingModule {}
